import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { Stack } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BackspaceIcon from "@mui/icons-material/Backspace";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { sensorManagement } from "../../../src/Dashboard/actions/apiActions";
import config from "config";
import AddAlertIcon from "@mui/icons-material/AddAlert";
import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import CloseIcon from "@mui/icons-material/Close";
import FunctionsIcon from "@mui/icons-material/Functions";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import { use } from "react";

// Example sensor data structure
// {
//   "SiteId": "kontram-customer-demo",
//   "EdgeUnitId": "os-level-customer-demo",
//   "HwId": "C4AC590000CCA201",
//   "DeviceType": "VEGAPULS Air",
//   "Config": {
//     "customerEventHubs": [
//       "dev-hub"
//     ],
//     "alarms": [
//       {
//         "name": "Temperature Alarm",
//         "valueName": "Temperature",
//         "category": "Device",
//         "warningHighLimit": 31.0,
//         "warningHighDescFi": "Lämpötila nousemassa",
//         "warningHighDescEng": "Temperature is rising",
//         "warningLowLimit": 10.0,
//         "warningLowDescFi": "Alhainen lämpötila",
//         "warningLowDescEng": "Low temperature",
//         "alarmLowLimit": 5.0,
//         "alarmLowDescFi": "Lämpötila lähellä nollaa",
//         "alarmLowDescEng": "Temperature near zero",
//         "alarmHighLimit": 40.0,
//         "alarmHighDescFi": "Lämpötila liian korkea",
//         "alarmHighDescEng": "Temperature too high",
//         "emailConfig": null,
//         "deltaThreshold": null
//       },
//       {
//         "name": "Inclination Alarm",
//         "valueName": "InclinationAngle",
//         "category": "Device",
//         "warningHighLimit": null,
//         "warningHighDescFi": null,
//         "warningHighDescEng": null,
//         "warningLowLimit": null,
//         "warningLowDescFi": null,
//         "warningLowDescEng": null,
//         "alarmLowLimit": null,
//         "alarmLowDescFi": null,
//         "alarmLowDescEng": null,
//         "alarmHighLimit": 10.0,
//         "alarmHighDescFi": "Kallistuskulma liian suuri",
//         "alarmHighDescEng": "Inclination angle too large",
//         "emailConfig": null,
//         "deltaThreshold": null
//       },
//       {
//         "name": "Fill",
//         "valueName": "FillLevel",
//         "category": "SampleCondition",
//         "warningHighLimit": null,
//         "warningHighDescFi": null,
//         "warningHighDescEng": null,
//         "warningLowLimit": 5.0,
//         "warningLowDescFi": "Tankki on tyhjä. Tilaa täydennys.",
//         "warningLowDescEng": "Tank is empty. Order refill.",
//         "alarmLowLimit": 15.0,
//         "alarmLowDescFi": "Tankki lahes tyhjä",
//         "alarmLowDescEng": "Tank almost empty",
//         "alarmHighLimit": null,
//         "alarmHighDescFi": null,
//         "alarmHighDescEng": null,
//         "emailConfig": "demo_alarm_config.json",
//         "deltaThreshold": null
//       }
//     ],
//     "valueConversions": [
//       {
//         "valueName": "FreeVolume",
//         "unit": "l",
//         "fromValueName": "Level",
//         "linearConversion": {
//           "targetMin": 0.0,
//           "targetMax": 1200.0,
//           "min": 0.0,
//           "max": 1.2
//         },
//         "inverseLinearConversion": null,
//         "linearRoundTank": null,
//         "inverseLinearRoundTank": null,
//         "siloConversion": null
//       },
//       {
//         "valueName": "FillLevel",
//         "unit": "%",
//         "fromValueName": "Level",
//         "linearConversion": null,
//         "inverseLinearConversion": {
//           "targetMin": 0.0,
//           "targetMax": 100.0,
//           "min": 0.0,
//           "max": 1.2
//         },
//         "linearRoundTank": null,
//         "inverseLinearRoundTank": null,
//         "siloConversion": null
//       },
//       {
//         "valueName": "UsedVolume",
//         "unit": "l",
//         "fromValueName": "Level",
//         "linearConversion": null,
//         "inverseLinearConversion": {
//           "targetMin": 0.0,
//           "targetMax": 1200.0,
//           "min": 0.0,
//           "max": 1.2
//         },
//         "linearRoundTank": null,
//         "inverseLinearRoundTank": null,
//         "siloConversion": null
//       }
//     ],
//     "valueRefinement": []
//   }
// }

const convertStringValuesToNumbersFromObject = (obj) => {
  if (!obj || typeof obj !== "object") {
    return null; // Return null if obj is not an object or is null
  }
  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    const num = parseFloat(value);
    result[key] = isNaN(num) ? value : num;
  });
  return result;
};

const SensorCard = ({
  selectedSensorSite,
  newSensor,
  editing,
  handleSetEditing,
  handleSetAddingSensor,
  sensor,
  fetchSensorsForEdgeUnit,
  copiedSensor,
  resetCopiedSensor,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [sensorState, setSensorState] = useState({
    hwId: "",
    sensorName: "",
    deviceType: "",
    siteId: selectedSensorSite?.customerId || "",
    edgeUnitId: selectedSensorSite?.deviceId || "",
    config: {
      // customerEventHubs: [],
      alarms: [],
      // valueConversions: [],
      valueRefinement: [],
    },
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ success: editing ? true : false, hwId: "", message: "" }); // in edit success is true and this lock save button until user makes a change

  useEffect(() => {
    if (sensor) {
      setSensorState((prev) => ({
        ...prev,
        ...sensor,
        config: {
          ...prev.config,
          ...(sensor.config || {}),
        },
      }));
    }
  }, [sensor]);

  useEffect(() => {
    if (copiedSensor) {
      // If there is a copied sensor, set it to the state
      setSensorState(copiedSensor);
      // Reset copiedSensor after setting it to the state
      resetCopiedSensor();
    }
  }, [copiedSensor]);

  console.log("sensorState", sensorState);

  useEffect(() => {
    if (selectedSensorSite) {
      setSensorState((prev) => ({
        ...prev,
        siteId: selectedSensorSite?.customerId || "",
        edgeUnitId: selectedSensorSite?.deviceId || "",
      }));
    }
  }, [selectedSensorSite]);

  const handleChange = (field, value) => {
    setSensorState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  };

  const handleEventHubChange = (e) => {
    // Split the input by commas and trim whitespace
    const hubsArray = e.target.value.split(",").map((hub) => hub);
    // Update the sensor state with the new array of event hubs
    setSensorState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        customerEventHubs: hubsArray,
      },
    }));
    setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  };

  const handleSensorAlarmChange = (field, value) => {
    setSensorState((prev) => {
      const updatedAlarms = prev.config.alarms.map((alarm, idx) => {
        if (idx === field.index) {
          return { ...alarm, [field.key]: value };
        }
        return alarm;
      });
      return {
        ...prev,
        config: {
          ...prev.config,
          alarms: updatedAlarms,
        },
      };
    });
    setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  };

  ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
  // const selectValueConversionType = (index, type) => {
  //   const updatedConversions = sensorState.config.valueConversions.map((conversion, idx) => {
  //     if (idx === index) {
  //       return {
  //         ...conversion,
  //         type,
  //         linearConversion: null,
  //         inverseLinearConversion: null,
  //         linearRoundTank: null,
  //         inverseLinearRoundTank: null,
  //         siloConversion: null,
  //       };
  //     }
  //     return conversion;
  //   });

  //   setSensorState((prev) => {
  //     return {
  //       ...prev,
  //       config: {
  //         ...prev.config,
  //         valueConversions: updatedConversions,
  //       },
  //     };
  //   });
  //   setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  // };

  ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
  // const handleValueConversionChange = (index, key, value, type) => {
  //   // if type is provided, then handle changes inside valueConversion object's type object (linearConversion, inverseLinearConversion, etc.)
  //   // else we handle changes inside valueConversion object directly
  //   if (type) {
  //     const updatedConversionTypeValues = sensorState.config.valueConversions.map((conversion, idx) => {
  //       if (idx === index) {
  //         return {
  //           ...conversion,
  //           [type]: {
  //             ...conversion[type],
  //             [key]: value,
  //           },
  //         };
  //       }
  //       return conversion;
  //     });
  //     setSensorState((prev) => ({
  //       ...prev,
  //       config: {
  //         ...prev.config,
  //         valueConversions: updatedConversionTypeValues,
  //       },
  //     }));
  //   } else {
  //     // if there is no type, handle changes directly in valueConversion object: valueName, unit or fromValueName
  //     const updatedConversions = sensorState.config.valueConversions.map((conversion, idx) => {
  //       if (idx === index) {
  //         return {
  //           ...conversion,
  //           [key]: value,
  //         };
  //       }
  //       return conversion;
  //     });
  //     setSensorState((prev) => ({
  //       ...prev,
  //       config: {
  //         ...prev.config,
  //         valueConversions: updatedConversions,
  //       },
  //     }));
  //   }
  //   setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  // };

  const addNewAlarm = () => {
    const alarmCount = sensorState.config?.alarms?.length || 0;
    setSensorState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        alarms: [
          ...(prev.config?.alarms || []),
          {
            name: "New Alarm " + (alarmCount + 1),
            valueName: "NewAlarm" + (alarmCount + 1),
            category: "",
            warningHighLimit: "",
            warningLowLimit: "",
            alarmHighLimit: "",
            alarmLowLimit: "",
            warningHighDescFi: "",
            warningHighDescEng: "",
            warningLowDescFi: "",
            warningLowDescEng: "",
          },
        ],
      },
    }));
  };

  const deleteAlarm = (index) => {
    // ask if sure
    if (
      !window.confirm(
        "Are you sure you want to remove this alarm? This action cannot be undone. Do note that this does not delete the alarm from the database until you save/update the sensor."
      )
    ) {
      return;
    }
    setSensorState((prev) => {
      const updatedAlarms = prev.config.alarms.filter((_, idx) => idx !== index);
      return {
        ...prev,
        config: {
          ...prev.config,
          alarms: updatedAlarms,
        },
      };
    });
    setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  };

  const copyAlarm = (index) => {
    setSensorState((prev) => {
      const alarmToCopy = prev.config.alarms[index];
      return {
        ...prev,
        config: {
          ...prev.config,
          alarms: [
            ...prev.config.alarms,
            { ...alarmToCopy, name: `Copy of ${alarmToCopy.name}`, valueName: `Copy-${alarmToCopy.valueName}` },
          ],
        },
      };
    });
    setMessage({ success: false, hwId: "", message: "" }); // Reset message state on change
  };

  ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
  // const addNewValueConversion = () => {
  //   // const conversionCount = sensorState.config.valueConversions.length;
  //   setSensorState((prev) => ({
  //     ...prev,
  //     config: {
  //       ...prev.config,
  //       valueConversions: [
  //         ...prev.config.valueConversions,
  //         {
  //           valueName: "",
  //           unit: "",
  //           fromValueName: "Level",
  //           type: "", // just for frontend, so we know if we use linear, inverse linear, round tank or silo conversion etc
  //         },
  //       ],
  //     },
  //   }));
  // };

  ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
  // const deleteValueConversion = (index) => {
  //   // ask if sure
  //   if (!window.confirm("Are you sure you want to remove this value conversion? This action cannot be undone.")) {
  //     return;
  //   }
  //   setSensorState((prev) => {
  //     const updatedConversions = prev.config.valueConversions.filter((_, idx) => idx !== index);
  //     return {
  //       ...prev,
  //       config: {
  //         ...prev.config,
  //         valueConversions: updatedConversions,
  //       },
  //     };
  //   });
  // };

  const clearAllFields = () => {
    // ask if sure
    if (!window.confirm("Are you sure you want to clear all fields? This action cannot be undone.")) {
      return; // If user cancels, do nothing
    }
    setSensorState({
      hwId: "",
      sensorName: "",
      deviceType: "",
      siteId: selectedSensorSite?.customerId || "",
      edgeUnitId: selectedSensorSite?.deviceId || "",
      config: {
        customerEventHubs: [],
        alarms: [],
        // valueConversions: [],       ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
        valueRefinement: [],
      },
    });
    setMessage({ success: false, hwId: "", message: "" }); // Reset message
  };

  const saveOrUpdateSensor = async (method) => {
    if (sensorState.hwId === "") {
      return;
    }
    setLoading(true);
    try {
      // Clone and modify sensorState
      const alteredSensorState = { ...sensorState };

      // Remove fields not accepted in PUT
      if (method === "PUT") {
        delete alteredSensorState.id;
        delete alteredSensorState.twinState;
      }

      ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
      // // Convert string values to numbers in valueConversions
      // if (alteredSensorState.config?.valueConversions?.length > 0) {
      //   alteredSensorState.config.valueConversions = alteredSensorState.config.valueConversions.map((conversion) => ({
      //     ...conversion,
      //     linearConversion: convertStringValuesToNumbersFromObject(conversion.linearConversion || null),
      //     inverseLinearConversion: convertStringValuesToNumbersFromObject(conversion.inverseLinearConversion || null),
      //     linearRoundTank: convertStringValuesToNumbersFromObject(conversion.linearRoundTank || null),
      //     inverseLinearRoundTank: convertStringValuesToNumbersFromObject(conversion.inverseLinearRoundTank || null),
      //     siloConversion: convertStringValuesToNumbersFromObject(conversion.siloConversion || null),
      //   }));
      // }

      // Call appropriate API method
      const response =
        method === "POST"
          ? await sensorManagement.post(config.CONFIGURATION_URL, alteredSensorState)
          : await sensorManagement.put(config.CONFIGURATION_URL, alteredSensorState);

      console.log(`Sensor ${method === "POST" ? "added" : "updated"} response:`, response);
      setLoading(false);

      if (response && (response.status === 200 || response.status === 201)) {
        setMessage({
          success: true,
          hwId: sensorState.hwId,
          message: `Sensor ${sensorState.hwId} ${method === "POST" ? "added" : "updated"} successfully!`,
        });
        fetchSensorsForEdgeUnit();
      } else {
        setMessage({
          success: false,
          hwId: sensorState.hwId,
          message: `Failed to ${method === "POST" ? "add" : "update"} sensor ${sensorState.hwId}.`,
        });
      }
    } catch (error) {
      console.error(`Error ${method === "POST" ? "adding" : "updating"} sensor:`, error);
      setMessage({
        success: false,
        hwId: "",
        message: `Failed to ${method === "POST" ? "add" : "update"} sensor ${sensorState.hwId}.`,
      });
      setLoading(false);
    }
  };

  const title = () => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">
          {editing ? sensorState.hwId : "Add New Sensor to: " + selectedSensorSite?.deviceName}
        </Typography>
        {editing && (
          <IconButton onClick={() => handleSetEditing(false)}>
            <CloseIcon />
          </IconButton>
        )}
        {newSensor && (
          <IconButton onClick={() => handleSetAddingSensor(false)}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: 1200, border: `1px solid ${theme.palette.primary.main}` }} elevation={1}>
      <CardHeader title={title()} /* subheader={sensorState.hwId && sensorState.hwId} */ />
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              label={editing ? "HwId change not allowed" : "HwId"}
              value={sensorState.hwId}
              onChange={(e) => handleChange("hwId", e.target.value)}
              fullWidth
              required
              disabled={editing}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              label="Sensor Name"
              value={sensorState.sensorName || ""}
              onChange={(e) => handleChange("sensorName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              freeSolo
              options={["VEGAPULS Air", "EasySTM"]}
              value={sensorState.deviceType || ""}
              onChange={(event, newValue) => handleChange("deviceType", newValue)}
              onInputChange={(event, newInputValue) => handleChange("deviceType", newInputValue)}
              size="small"
              renderInput={(params) => <TextField {...params} label="Device Type" />}
            />
          </Grid>
          {/* <Grid size={{ xs: 12, md: 12 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                // variant="standard"
                size="small"
                label="Customer Event Hubs"
                value={sensorState.config.customerEventHubs.join(",")}
                onChange={(e) => handleEventHubChange(e)}
                fullWidth
              />
              <Tooltip title="Comma separated, no spaces. Example: hub1,hub2,hub3">
                <InfoIcon />
              </Tooltip>
            </Box>
          </Grid> */}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
          <Typography variant="h6">Alarms</Typography>
          <IconButton size="large" onClick={() => addNewAlarm()}>
            <AddAlertIcon />
          </IconButton>
        </Box>
        {sensorState.config?.alarms?.length > 0 &&
          sensorState.config.alarms.map((alarm, idx) => (
            <Accordion
              defaultExpanded={newSensor}
              key={idx}
              sx={{
                border: `1px solid ${theme.palette.primary.dark}`,
                borderRadius: 1,
                marginBottom: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-content-${idx}`}
                id={`panel-header-${idx}`}
                sx={{ bgcolor: theme.palette.background.paper }}
              >
                <NotificationImportantIcon sx={{ mr: 1 }} />
                <Typography sx={{ fontSize: "18px" }}>{alarm.name || "Unnamed Alarm"}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: theme.palette.background.paper }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  <IconButton size="large" color="info" onClick={() => copyAlarm(idx)}>
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton size="large" color="error" onClick={() => deleteAlarm(idx)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
                <Paper elevation={3} sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Alarm Display Name
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Alarm Display Name" : ""}
                          value={alarm.name || ""}
                          onChange={(e) => handleSensorAlarmChange({ index: idx, key: "name" }, e.target.value)}
                          fullWidth
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Value Name
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Value Name" : ""}
                          value={alarm.valueName || ""}
                          fullWidth
                          onChange={(e) => handleSensorAlarmChange({ index: idx, key: "valueName" }, e.target.value)}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Category
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Category" : ""}
                          value={alarm.category || ""}
                          fullWidth
                          onChange={(e) => handleSensorAlarmChange({ index: idx, key: "category" }, e.target.value)}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Email Config
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Email Config" : ""}
                          value={alarm.emailConfig || ""}
                          fullWidth
                          onChange={(e) => handleSensorAlarmChange({ index: idx, key: "emailConfig" }, e.target.value)}
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Delta Threshold
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Delta Threshold" : ""}
                          value={alarm.deltaThreshold || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "deltaThreshold" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider sx={{ m: 1 }} />
                  <Typography variant="h6" color="warning">
                    Warning
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        // variant="standard"
                        size="small"
                        label="Warning Low Limit"
                        value={alarm.warningLowLimit || ""}
                        fullWidth
                        color="warning"
                        onChange={(e) =>
                          handleSensorAlarmChange({ index: idx, key: "warningLowLimit" }, e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        // variant="standard"
                        size="small"
                        label="Warning High Limit"
                        value={alarm.warningHighLimit || ""}
                        fullWidth
                        color="warning"
                        onChange={(e) =>
                          handleSensorAlarmChange({ index: idx, key: "warningHighLimit" }, e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Warning Low Description (FI)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Warning Low Description (FI)" : ""}
                          value={alarm.warningLowDescFi || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "warningLowDescFi" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Warning High Description (FI)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Warning High Description (FI)" : ""}
                          value={alarm.warningHighDescFi || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "warningHighDescFi" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Warning Low Description (ENG)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Warning Low Description (ENG)" : ""}
                          value={alarm.warningLowDescEng || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "warningLowDescEng" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Warning High Description (ENG)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Warning High Description (ENG)" : ""}
                          value={alarm.warningHighDescEng || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "warningHighDescEng" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ m: 1 }} />
                  <Typography variant="h6" color="error">
                    Alarm
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        size="small"
                        label="Alarm Low Limit"
                        value={alarm.alarmLowLimit || ""}
                        fullWidth
                        onChange={(e) => handleSensorAlarmChange({ index: idx, key: "alarmLowLimit" }, e.target.value)}
                        color="error"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        size="small"
                        label="Alarm High Limit"
                        value={alarm.alarmHighLimit || ""}
                        fullWidth
                        onChange={(e) => handleSensorAlarmChange({ index: idx, key: "alarmHighLimit" }, e.target.value)}
                        color="error"
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Alarm Low Description (FI)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Alarm Low Description (FI)" : ""}
                          value={alarm.alarmLowDescFi || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "alarmLowDescFi" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Alarm High Description (FI)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Alarm High Description (FI)" : ""}
                          value={alarm.alarmHighDescFi || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "alarmHighDescFi" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Alarm Low Description (ENG)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Alarm Low Description (ENG)" : ""}
                          value={alarm.alarmLowDescEng || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "alarmLowDescEng" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                        >
                          Alarm High Description (ENG)
                        </Typography>
                        <TextField
                          variant="standard"
                          size="small"
                          label={isMobile ? "Alarm High Description (ENG)" : ""}
                          value={alarm.alarmHighDescEng || ""}
                          fullWidth
                          onChange={(e) =>
                            handleSensorAlarmChange({ index: idx, key: "alarmHighDescEng" }, e.target.value)
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}

        <Divider sx={{ my: 2 }} />

        {/**************
         VALUE CONVERSIONS 

         ///// VALUE CONVERSIONS COMMENTED OUT FOR NOW, THEY WORK IN FRONT BUT NOT IN USE YET. DONT REMOVE
         ***************/}
        {/*
          <>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
              <Typography variant="h6">Value Conversions</Typography>
              <IconButton size="large" onClick={() => addNewValueConversion()}>
                <AddCircleIcon />
              </IconButton>
            </Box>
            {sensorState.config?.valueConversions?.length > 0 &&
              sensorState.config.valueConversions.map((conversion, idx) => (
                <Accordion
                  defaultExpanded={newSensor}
                  key={idx}
                  sx={{
                    border: `1px solid ${theme.palette.primary.dark}`,
                    borderRadius: 1,
                    marginBottom: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-content-${idx}`}
                    id={`panel-header-${idx}`}
                    sx={{ bgcolor: theme.palette.background.paper }}
                  >
                    <FunctionsIcon sx={{ mr: 1 }} />
                    <Typography sx={{ fontSize: "18px" }}>{conversion.valueName || "Unnamed Conversion"}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: theme.palette.background.paper }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                      <IconButton size="large" color="error" onClick={() => deleteValueConversion(idx)}>
                        <DeleteForeverIcon />
                      </IconButton>
                    </Box>
                    <Paper elevation={3} sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                            >
                              Value Name
                            </Typography>
                            <Autocomplete
                              freeSolo
                              options={["FreeVolume", "FillLevel", "UsedVolume", "RemainingFeed", "SiloRemainingFeed"]}
                              value={conversion.valueName || ""}
                              label={isMobile ? "Value Name" : ""}
                              size="small"
                              fullWidth
                              onChange={(event, newValue) => handleValueConversionChange(idx, "valueName", newValue)}
                              onInputChange={(event, newInputValue) =>
                                handleValueConversionChange(idx, "valueName", newInputValue)
                              }
                              renderInput={(params) => <TextField variant="standard" {...params} />}
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                            >
                              Unit
                            </Typography>
                            <Autocomplete
                              freeSolo
                              options={["%", "l", "kg"]}
                              value={conversion.unit || ""}
                              label={isMobile ? "Unit" : ""}
                              size="small"
                              fullWidth
                              onChange={(event, newValue) => handleValueConversionChange(idx, "unit", newValue)}
                              onInputChange={(event, newInputValue) =>
                                handleValueConversionChange(idx, "unit", newInputValue)
                              }
                              renderInput={(params) => <TextField variant="standard" {...params} />}
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                            >
                              From Value Name
                              <Tooltip title="Calculation is based on this value">
                                <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                              </Tooltip>
                            </Typography>
                            <TextField
                              variant="standard"
                              size="small"
                              label={isMobile ? "From Value Name" : ""}
                              value={conversion.fromValueName || ""}
                              onChange={(e) => handleValueConversionChange(idx, "fromValueName", e.target.value)}
                              fullWidth
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                              sx={{ minWidth: 200, display: { xs: "none", lg: "block" } }}
                            >
                              Type
                            </Typography>
                            <Select
                              variant="standard"
                              size="small"
                              fullWidth
                              label={isMobile ? "Type" : ""}
                              value={conversion.type || ""}
                              onChange={(e) => selectValueConversionType(idx, e.target.value)}
                            >
                              <MenuItem value="linearConversion">Linear</MenuItem>
                              <MenuItem value="inverseLinearConversion">Inverse Linear</MenuItem>
                              <MenuItem value="linearRoundTank">Linear Round Tank</MenuItem>
                              <MenuItem value="inverseLinearRoundTank">Inverse Linear Round Tank</MenuItem>
                              <MenuItem value="siloConversion">Silo</MenuItem>
                            </Select>
                          </Box>
                        </Grid>
                        {conversion.type && (
                          <>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
                                <TextField
                                  size="small"
                                  label="Target Min"
                                  value={conversion[conversion.type]?.targetMin || ""}
                                  onChange={(e) =>
                                    handleValueConversionChange(idx, "targetMin", e.target.value, conversion.type)
                                  }
                                  fullWidth
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
                                <TextField
                                  size="small"
                                  label="Target Max"
                                  value={conversion[conversion.type]?.targetMax || ""}
                                  onChange={(e) =>
                                    handleValueConversionChange(idx, "targetMax", e.target.value, conversion.type)
                                  }
                                  fullWidth
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                                <TextField
                                  size="small"
                                  label="Min"
                                  value={conversion[conversion.type]?.min || ""}
                                  onChange={(e) =>
                                    handleValueConversionChange(idx, "min", e.target.value, conversion.type)
                                  }
                                  fullWidth
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                                <TextField
                                  size="small"
                                  label="Max"
                                  value={conversion[conversion.type]?.max || ""}
                                  onChange={(e) =>
                                    handleValueConversionChange(idx, "max", e.target.value, conversion.type)
                                  }
                                  fullWidth
                                />
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              ))}
          </>
        */}

        <CardActions sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          {message.message && <Typography color={message.success ? "success" : "error"}>{message.message}</Typography>}
          {loading && <Typography color="warning">Saving sensor...</Typography>}
          <Box sx={{ display: "flex", gap: 1, flex: 1, justifyContent: "flex-end" }}>
            <Button color="error" onClick={clearAllFields}>
              Clear All fields
            </Button>
            {newSensor && (
              <Button
                variant="contained"
                onClick={() => saveOrUpdateSensor("POST")}
                loading={loading}
                disabled={message.success || loading || !sensorState.hwId}
              >
                {newSensor && !message.success && "Add Sensor"}
                {message.success && "Set unique HwId"}
              </Button>
            )}
            {editing && (
              <Button
                variant="contained"
                onClick={() => saveOrUpdateSensor("PUT")}
                loading={loading}
                disabled={message.success || loading || !sensorState.hwId}
              >
                {editing && !message.success && "Update Sensor"}
                {message.success && "No changes to save"}
              </Button>
            )}
          </Box>
        </CardActions>
      </CardContent>
    </Card>
  );
};

export default SensorCard;
